/**
 * Consultas e gravação do histórico de solicitações pelo TanStack Query.
 *
 * Papel: `useSolicitacoes` lê o histórico do próprio usuário, `useCriarSolicitacao`
 * envia uma solicitação e manda o histórico se atualizar, e `useFotoDaSolicitacao`
 * busca a imagem de uma linha.
 *
 * Como usar: toda chave deste módulo começa por `CHAVE_SOLICITACOES`. A invalidação
 * depois do envio atinge a lista inteira, e não o cache de cada foto: a foto de uma
 * solicitação já gravada não muda. `staleTime` da foto é longo pelo mesmo motivo.
 */
import { type NovaSolicitacao } from '@concierge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  buscarFotoDaSolicitacao,
  criarSolicitacao,
  listarSolicitacoes,
} from '../api/solicitacoes';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_SOLICITACOES = ['solicitacao-de-compra'] as const;

/** Chave da lista do histórico. */
const CHAVE_LISTA = [...CHAVE_SOLICITACOES, 'lista'] as const;

/** Quanto tempo a imagem de uma solicitação permanece fresca no cache. */
const VALIDADE_DA_FOTO_EM_MS = 30 * 60 * 1000;

/**
 * Lê o histórico do próprio usuário.
 *
 * @returns Resultado da consulta, da solicitação mais recente para a mais antiga.
 */
export function useSolicitacoes() {
  return useQuery({
    queryKey: CHAVE_LISTA,
    queryFn: ({ signal }) => listarSolicitacoes(signal),
  });
}

/**
 * Envia uma solicitação e atualiza o histórico ao receber a confirmação.
 *
 * @returns A mutação, que recebe os cinco campos já validados.
 */
export function useCriarSolicitacao() {
  const clienteDeConsultas = useQueryClient();
  return useMutation({
    mutationFn: (dados: NovaSolicitacao) => criarSolicitacao(dados),
    onSuccess: () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_LISTA }),
  });
}

/**
 * Lê a imagem de uma solicitação.
 *
 * Quem chama é a miniatura de uma linha que tem `temFoto`; a linha sem imagem não
 * monta esta consulta, e por isso não há requisição para ela.
 *
 * @param id - Identificador da solicitação.
 * @returns Resultado da consulta, com `foto` nula quando não há imagem.
 */
export function useFotoDaSolicitacao(id: string) {
  return useQuery({
    queryKey: [...CHAVE_SOLICITACOES, 'foto', id],
    queryFn: ({ signal }) => buscarFotoDaSolicitacao(id, signal),
    staleTime: VALIDADE_DA_FOTO_EM_MS,
  });
}
